import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import AuthShell from "@/components/AuthShell";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { useHoneypot } from "@/lib/honeypot";
import { graphqlClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";

const REQUEST_PASSWORD_RESET = graphql(`
  mutation RequestPasswordReset($emailAddress: String!, $website: String, $formElapsedMs: Int) {
    requestPasswordReset(input: { emailAddress: $emailAddress, website: $website, formElapsedMs: $formElapsedMs }) {
      success
      errors
    }
  }
`);

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const honeypot = useHoneypot();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // The server always reports generic success (anti-enumeration), so on a
      // non-error response we switch to the "check your email" state regardless.
      await graphqlClient.request(REQUEST_PASSWORD_RESET, {
        emailAddress: email,
        website: honeypot.website,
        formElapsedMs: honeypot.elapsedMs(),
      });
      setSent(true);
    } catch (err) {
      console.error("RequestPasswordReset error:", err);
      toast({ title: t("auth.forgotPassword.toast.errorTitle"), description: err instanceof Error ? err.message : t("auth.forgotPassword.toast.genericError"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
        <Card className="border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{t("auth.forgotPassword.title")}</CardTitle>
            <CardDescription>
              {sent ? t("auth.forgotPassword.sentDescription") : t("auth.forgotPassword.description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <p className="text-center text-sm text-muted-foreground">
                {t("auth.forgotPassword.sentBody")}
              </p>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <input {...honeypot.inputProps} />
                <div className="space-y-2">
                  <Label htmlFor="email">{t("auth.forgotPassword.emailLabel")}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("auth.forgotPassword.emailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? t("auth.forgotPassword.submitting") : t("auth.forgotPassword.submit")}
                </Button>
              </form>
            )}

            <p className="text-center text-sm text-muted-foreground mt-6">
              <Link to="/signin" className="text-foreground hover:underline font-medium">{t("auth.forgotPassword.backToSignIn")}</Link>
            </p>
          </CardContent>
        </Card>
    </AuthShell>
  );
};

export default ForgotPassword;
